import { PaymentIntent } from '@stripe/stripe-js';
import { useStripe } from '@stripe/react-stripe-js';
import { FC, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { getPayStripePaymentIntent } from 'selectors/pay';
import { getUserData } from 'selectors/user';
import { IEvent } from 'store/eventPage/types';
import { IPage } from 'store/donatePage/types';
import theme from 'config/theme';
import { useNavigate, useParams } from 'react-router-dom';
import { clearCheckoutIdempotencyKey } from 'services/storage';
import { getPageImageUrl } from 'helpers/imageUrls';
import Loader from 'components/Loader';
import { storeStripePaymentIntent } from 'store/pay/actions';
import { Confirmation } from '../shared/Confirmation';
import { StripeCarousel } from './StripeCarousel';
import { OathRecurringInterval } from 'store/pay/types';
import { getUrlParams } from 'helpers/utils';
import { trackPixel, trackPurchaseOnce } from 'helpers/pixel';
import { trackCheckoutConfirmationViewed } from 'helpers/mixpanelEvents';
import { CAROUSEL_CLIENT_IDS } from './constants';
import { getIsStatesProjectRedirectPage, STATES_PROJECT_REDIRECT } from 'helpers/donate';
import { usePartnerTrackingConversion } from 'hooks/usePartnerTracking';

const getIsDonationSuccessful = (paymentIntent: PaymentIntent) =>
  paymentIntent.status === 'succeeded';
const getIsDonationPending = (paymentIntent: PaymentIntent) =>
  paymentIntent.status === 'processing';
const getDonationRequiresAction = (paymentIntent: PaymentIntent) =>
  paymentIntent.status === 'requires_action';
const getDate = (paymentIntent: PaymentIntent) => {
  const dateObj = paymentIntent ? new Date(paymentIntent.created * 1000) : new Date();
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};
const getTipAmount = (totalDonatedCents: number, totalSelectedDollars: number) =>
  Math.round(totalDonatedCents - totalSelectedDollars * 100);

interface Props {
  donationAmount: number;
  isRecurring: boolean;
  numRecipients: number; // replace with payment intent metadata when ready
  page?: IPage | IEvent;
  recurringInterval?: OathRecurringInterval;
}

export const StripeConfirmation: FC<Props> = ({
  donationAmount,
  isRecurring,
  numRecipients,
  page,
  recurringInterval = 'month',
}) => {
  const stripe = useStripe();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { urlPartner } = useParams();
  const { paymentIntent, user } = useAppSelector(state => ({
    paymentIntent: getPayStripePaymentIntent(state),
    user: getUserData(state),
  }));

  useEffect(() => {
    if (!stripe || paymentIntent) {
      return;
    }
    // Retrieve the "payment_intent_client_secret" query parameter appended to
    // your return_url by Stripe.js
    const clientSecret = new URLSearchParams(window.location.search).get(
      'payment_intent_client_secret'
    );
    if (!clientSecret) {
      navigate(`/donate?p=${urlPartner}`);
      return;
    }
    // Retrieve the PaymentIntent
    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      if (!paymentIntent) {
        return;
      }
      dispatch(storeStripePaymentIntent(paymentIntent));
    });
  }, [stripe]);

  useEffect(() => {
    clearCheckoutIdempotencyKey();
  }, []);

  const { urlPartner: urlPartnerParam, urlDonorSelectedMethod } = getUrlParams();
  // This route only ever sees the Stripe pair — pledges land on /donate/confirmation/pledge.
  const paymentMethod =
    urlDonorSelectedMethod === 'credit_card' || urlDonorSelectedMethod === 'ach'
      ? urlDonorSelectedMethod
      : undefined;

  const isDonationSuccessful = paymentIntent ? getIsDonationSuccessful(paymentIntent) : false;
  usePartnerTrackingConversion(
    page,
    isDonationSuccessful,
    paymentIntent?.id,
    paymentIntent?.amount
  );

  const isStatesProjectPage = getIsStatesProjectRedirectPage(page?.client_id, urlPartnerParam);

  useEffect(() => {
    if (!paymentIntent || !isDonationSuccessful || !isStatesProjectPage) return;
    trackPurchaseOnce(paymentIntent.id, { amount: paymentIntent.amount, unit: 'cents' });
    trackPixel('universalPixel');
    trackCheckoutConfirmationViewed({
      is_recurring: isRecurring,
      amount: donationAmount,
      client_id: page?.client_id,
      is_stripe: true,
      payment_processor: 'Stripe',
      payment_method: paymentMethod,
    });
    window.location.replace(STATES_PROJECT_REDIRECT.REDIRECT_URL);
  }, [paymentIntent]);

  if (!paymentIntent) {
    return (
      <div className="confirmation">
        <div className="loader-container">
          <Loader width={60} height={60} />
        </div>
      </div>
    );
  }

  if (isStatesProjectPage && isDonationSuccessful) {
    return null;
  }

  const { urlExpressCheckout } = getUrlParams();
  const isExpressCheckout = urlExpressCheckout === 'true';
  const isOathClientDonatePage =
    !!page &&
    !page.event_id &&
    page.client_id != null &&
    CAROUSEL_CLIENT_IDS.includes(page.client_id);
  const tip = donationAmount ? getTipAmount(paymentIntent.amount, donationAmount) : null; // need to add tip to payment intent metaData
  const donorFullName = user ? `${user.first_name} ${user.last_name}` : '';
  const isEvent = !!page?.event_id;
  const textColor = isEvent ? 'white' : 'inkBlue';
  const headerBackground = isEvent ? theme.colors.oathBlue : undefined;
  const isDonationPending = getIsDonationPending(paymentIntent);
  const isActionRequired = getDonationRequiresAction(paymentIntent);
  const date = getDate(paymentIntent);
  const pageImage = urlPartner && page ? getPageImageUrl(page) : '';
  const showStripeCarousel =
    isOathClientDonatePage && isDonationSuccessful && !isExpressCheckout && donationAmount;

  return (
    <div className="confirmation stripe-confirmation-page">
      <Confirmation
        donationAmount={paymentIntent.amount}
        page={page}
        date={date}
        donorFullName={donorFullName}
        headerBackground={headerBackground}
        isActionRequired={isActionRequired}
        isDonationPending={isDonationPending}
        isDonationSuccessful={isDonationSuccessful}
        isEvent={isEvent}
        isRecurring={isRecurring}
        numRecipients={numRecipients}
        pageImage={pageImage}
        paymentIntent={paymentIntent}
        paymentMethod={paymentMethod}
        receiptEmail={paymentIntent.receipt_email}
        recurringInterval={recurringInterval}
        textColor={textColor}
        tip={tip}
      />
      {showStripeCarousel && (
        <StripeCarousel
          donationAmount={donationAmount}
          page={page as IPage}
          recurringInterval={isRecurring ? recurringInterval : 'one_time'}
        />
      )}
    </div>
  );
};
